import { ListView, TopBarRoute } from "../../../components/templates";
import { useNavigate, useParams } from "react-router";
import {
  ActionMenu,
  Card,
  Field,
  Icon,
  Input,
  LabelledCheckbox,
  Select,
  Stack,
  Title,
  Tooltip
} from "@introist/react-foundation/v2";

import { DragHandle, UpsertFormProps, UpsertModal, useSearch } from "foundation";
import { useMemo, useState } from "react";
import { omit } from "lodash";
import { KeyInput, simplifyKeyValue } from "../components/KeyInput";
import { DatasetField } from "../index";
import { useDatasetFields } from "../hooks/useDatasetFields";
import { ArchivedFieldsModal } from "../containers/ArchivedFieldsModal";
import { useDatasetDef, useDatasetDefs } from "../hooks/useDatasetDefs";
import { useEmployeeFields } from "modules/employees/hooks/useEmployeeFields";

export const DatasetDefRoute = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { def: dataset } = useDatasetDef({ id: id! });

  const { fields, create, update, archive, reorder } = useDatasetFields({ datasetId: id! });

  const { filtered, search, setSearch } = useSearch(fields, { fields: ["title", "key"] });

  const [editOpen, setEditOpen] = useState(false);
  const [editEntry, setEditEntry] = useState<DatasetField | undefined>();

  const [archivedOpen, setArchivedOpen] = useState(false);

  return (
    <TopBarRoute
      paths={[
        { key: "datasets", title: "Datasets", onClick: () => navigate("/datasets") },
        { key: "dataset", title: dataset?.plural || "" }
      ]}
    >
      <Card style={{ maxWidth: "750px", width: "750px", margin: "0 auto" }}>
        <Stack vertical gap="xLarge" style={{ flex: 1 }}>
          <ListView.Header
            title="Dataset Fields"
            iconName="attributeFilled"
            count={filtered?.length}
            onCreate={() => {
              setEditOpen(true);
              setEditEntry(undefined);
            }}
            actions={[
              {
                key: "showarchived",
                title: "Archived fields",
                onClick: () => setArchivedOpen(true)
              }
            ]}
            search={{
              search,
              setSearch
            }}
          />
          <ListView.OrderableList
            data={filtered}
            //@ts-ignore
            onReorder={!search ? reorder : undefined}
            renderRow={(f: DatasetField) => (
              <Card>
                <Stack>
                  <DragHandle />
                  <Stack>
                    <Title variant="bold">{f.title}</Title>
                    <Title>{f.key}</Title>
                    {f.isRequired && (
                      <Tooltip tooltip="This field is required">
                        <Icon name="exclamationCircle" style={{ width: 12, height: 12 }} dimmed />
                      </Tooltip>
                    )}
                    {f.isUnique && (
                      <Tooltip tooltip="Values for this field must be unique">
                        <Icon name="spark" style={{ width: 12, height: 12 }} dimmed />
                      </Tooltip>
                    )}
                  </Stack>
                  <div
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    style={{ marginLeft: "auto" }}
                  >
                    <ActionMenu
                      options={[
                        {
                          key: "edit",
                          title: "Edit",
                          startAdornmentIcon: "pencil",
                          onClick: () => {
                            setEditOpen(true);
                            setEditEntry(f);
                          }
                        },
                        {
                          key: "archive",
                          title: "Archive",
                          startAdornmentIcon: "trash",
                          onClick: () => archive(f.id)
                        }
                      ]}
                    />
                  </div>
                </Stack>
              </Card>
            )}
          />
        </Stack>
      </Card>

      <UpsertModal
        open={editOpen}
        entry={editEntry}
        onCreate={async entry => {
          await create(entry);
        }}
        onUpdate={entry => {
          return update({
            id: entry.id,
            updates: omit(entry, "id", "workspaceId", "datasetId", "key")
          });
        }}
        onClose={() => {
          setEditOpen(false);
          setEditEntry(undefined);
        }}
        Form={FieldForm}
        entryName="field"
      />
      <ArchivedFieldsModal
        open={archivedOpen}
        onClose={() => setArchivedOpen(false)}
        datasetId={id!}
      />
    </TopBarRoute>
  );
};

const FieldForm = ({ value, onChange, isEdit }: UpsertFormProps<DatasetField>) => {
  const { datasets } = useDatasetDefs();
  const { fields: employeeFields } = useEmployeeFields();
  const employeeMatchOptions = useMemo(
    () => [
      { key: "id", title: "Employee ID" },
      ...(employeeFields ?? []).map(f => ({ key: f.key, title: f.title }))
    ],
    [employeeFields]
  );

  return (
    <Stack vertical style={{ width: "300px" }}>
      <Field title="Title">
        <Input
          value={value.title}
          onChange={title => onChange({ ...value, title })}
          onBlur={() => {
            onChange({ ...value, key: simplifyKeyValue(value.title) });
          }}
          autoFocus
        />
      </Field>
      <Field title="Key">
        <KeyInput
          size="small"
          value={value.key}
          onChange={key => onChange({ ...value, key })}
          disabled={isEdit}
        />
      </Field>
      <Field title="Type">
        <Select
          size="small"
          style={{ width: "100%" }}
          value={value.type}
          onSelect={opt => onChange({ ...value, type: opt.key })}
          options={[
            { key: "text", title: "Text" },
            { key: "email", title: "Email" },
            { key: "number", title: "Number" },
            { key: "date", title: "Date" },
            { key: "reference", title: "Data link" },
            { key: "employee", title: "Employee" }
          ]}
          disabled={isEdit}
        />
      </Field>
      {value.type === "reference" && (
        <Field title="Linked Dataset">
          <Select
            size="small"
            style={{ width: "100%" }}
            value={value.ref ?? undefined}
            onSelect={opt => onChange({ ...value, ref: opt.key })}
            options={(datasets ?? []).map(d => ({ key: d.id, title: d.title }))}
            disabled={isEdit}
          />
        </Field>
      )}
      {value.type === "employee" && (
        <Field title="Match Employee By">
          <Select
            size="small"
            searchable
            style={{ width: "100%" }}
            value={value.employeeMatchField ?? undefined}
            onSelect={opt => onChange({ ...value, employeeMatchField: opt.key })}
            options={employeeMatchOptions}
            placeholder="Select employee field"
            disabled={isEdit}
          />
        </Field>
      )}
      <LabelledCheckbox
        label="Required"
        checked={value.isRequired}
        onChange={isRequired => onChange({ ...value, isRequired })}
      />
      <LabelledCheckbox
        label="Unique"
        checked={value.isUnique}
        onChange={isUnique => onChange({ ...value, isUnique })}
      />
    </Stack>
  );
};
